import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Users,
  Heart,
  TrendingUp,
  Globe,
  Megaphone,
  Sprout,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useState } from 'react';

const commitmentIcons = [
  Shield,      // Constitution
  Users,       // Unity
  Heart,       // Health & Education
  TrendingUp,  // Investment
  Globe,       // ASEAN
  Megaphone,   // Voice of voiceless
  Sprout,      // Future generations
];

export function Vision() {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [expandedCommitment, setExpandedCommitment] = useState<number | null>(null);

  const visionStatement = t('vision.statement');
  const commitmentsTitle = t('vision.commitmentsTitle');
  const commitments = Array.from({ length: 7 }, (_, i) => ({
    number: i + 1,
    title: t(`vision.commitments.${i}.title`),
    shortTitle: t(`vision.commitments.${i}.shortTitle`),
    desc: t(`vision.commitments.${i}.desc`),
  }));

  const toggleCommitment = (index: number) => {
    setExpandedCommitment(expandedCommitment === index ? null : index);
  };

  return (
    <section id="vision" className="py-24 bg-gradient-to-b from-white via-sky-50/30 to-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-sky-200 to-transparent" />
      <div className="absolute top-20 -left-32 w-64 h-64 bg-sky-100 rounded-full blur-3xl opacity-40" />
      <div className="absolute bottom-20 -right-32 w-96 h-96 bg-sky-50 rounded-full blur-3xl opacity-30" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Vision Statement */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={inView ? { scale: 1, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-sky-500 to-sky-700 rounded-2xl mb-8 shadow-lg shadow-sky-200"
          >
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </motion.div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-campaign-black mb-8">
            {t('vision.title')}
          </h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-4xl mx-auto"
          >
            <p className="text-xl md:text-2xl leading-relaxed text-gray-700 font-light italic">
              "{visionStatement}"
            </p>
            <div className="mt-6 w-24 h-1 bg-gradient-to-r from-sky-500 to-sky-700 mx-auto rounded-full" />
          </motion.div>
        </motion.div>

        {/* Commitments Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-12"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-heading font-bold text-campaign-black mb-2">
              {commitmentsTitle}
            </h3>
            <div className="w-16 h-1 bg-sky-600 mx-auto rounded-full" />
          </div>

          {/* Commitments Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {commitments.map((commitment, index) => {
              const Icon = commitmentIcons[index];
              const isExpanded = expandedCommitment === index;

              return (
                <motion.div
                  key={commitment.number}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="group"
                >
                  <button
                    onClick={() => toggleCommitment(index)}
                    className="w-full text-left bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-sky-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                  >
                    {/* Header */}
                    <div className="p-6">
                      <div className="flex items-start gap-4 mb-3">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-700 rounded-xl flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform duration-300">
                          <Icon size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full">
                              {String(commitment.number).padStart(2, '0')}
                            </span>
                          </div>
                          <h4 className="text-lg font-heading font-bold text-campaign-black leading-tight line-clamp-2">
                            {commitment.shortTitle}
                          </h4>
                        </div>
                      </div>

                      {/* Preview text (always visible) */}
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                        {commitment.desc}
                      </p>

                      {/* Expand indicator */}
                      <div className="mt-3 flex items-center justify-center text-sky-600">
                        {isExpanded ? (
                          <ChevronUp size={20} className="transition-transform duration-300" />
                        ) : (
                          <ChevronDown size={20} className="transition-transform duration-300" />
                        )}
                      </div>
                    </div>

                    {/* Expanded content */}
                    <motion.div
                      initial={false}
                      animate={{
                        height: isExpanded ? 'auto' : 0,
                        opacity: isExpanded ? 1 : 0,
                      }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2 bg-sky-50/50 border-t border-sky-100">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {commitment.desc}
                        </p>
                      </div>
                    </motion.div>
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* 7th Commitment - Full width feature */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-8"
          >
            <div className="bg-gradient-to-r from-sky-600 to-sky-700 rounded-2xl p-8 md:p-12 text-white shadow-xl relative overflow-hidden">
              {/* Decorative background */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Sprout size={32} />
                  </div>
                  <div>
                    <span className="inline-block text-xs font-bold bg-white/20 px-3 py-1 rounded-full mb-2">
                      07
                    </span>
                    <h4 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                      {commitments[6].title}
                    </h4>
                  </div>
                </div>
                <p className="text-lg md:text-xl leading-relaxed text-white/90 max-w-4xl">
                  {commitments[6].desc}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
